import {
  ClearItemsRespSchema,
  ClearTopicsRespSchema,
  type DiscoverReq,
  type DiscoverResp,
  DiscoverRespSchema,
  type ImportStoriesResp,
  ImportStoriesRespSchema,
  type ImportTopicsResp,
  ImportTopicsRespSchema,
  ItemsRespSchema,
  type PulseResp,
  PulseRespSchema,
  RegisterRespSchema,
  type SessionResp,
  SessionRespSchema,
  StateRespSchema,
  type SuggestSimilarResp,
  SuggestSimilarRespSchema,
  ThreadRespSchema,
  TopicSparklineRespSchema,
  type TopicSuggestion,
} from '../../api/schemas.js';
import { type Topic, TopicSchema } from '../../db/schemas.js';
import { effectiveCategoryFilter } from '../features/feed/view-filters.js';
import { noteCheckState } from '../features/notifications/check-announce.js';
import { noteState } from '../features/notifications/notifications.js';
import { apiBase } from './config.js';
import { appStore, isPendingPulse, pollTick } from './stores.js';

/**
 * A non-2xx response, carrying the parsed body so a caller can read a structured
 * `code` (e.g. the `over_capacity` 409, NM-201) rather than only the message.
 * Extends `Error`, so every existing `instanceof Error` / `err.message` path is
 * unaffected.
 */
export class RequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'RequestError';
  }
}

/** The `over_capacity` 409 payload (NM-201, docs/48 FR-48.4/48.5). */
export interface OverCapacity {
  kind: 'custom' | 'catalog';
  cap: number;
  used: number;
}

/** Read the `over_capacity` shape off a rejection, or null if it is a different error. */
export function overCapacityOf(err: unknown): OverCapacity | null {
  if (!(err instanceof RequestError) || err.status !== 409) return null;
  const body = err.body;
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  if (b['code'] !== 'over_capacity') return null;
  if (
    (b['kind'] !== 'custom' && b['kind'] !== 'catalog') ||
    typeof b['cap'] !== 'number' ||
    typeof b['used'] !== 'number'
  )
    return null;
  return { kind: b['kind'], cap: b['cap'], used: b['used'] };
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  // Prefix the API origin in a split-origin deploy (NM-188); empty = same-origin.
  // `credentials: 'include'` so the session cookie rides a cross-origin fetch (the
  // frontend + API hosts are same-site, so a SameSite=Lax cookie is sent). Set
  // Content-Type only when there is a body, so a GET poll stays a "simple" request
  // and does not trigger a CORS preflight on every 4s tick.
  const res = await fetch(apiBase() + path, {
    credentials: 'include',
    headers: init?.body !== undefined && init.body !== null ? { 'Content-Type': 'application/json' } : {},
    ...init,
  });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string'
        ? body.error
        : `request failed (${res.status})`;
    throw new RequestError(message, res.status, body);
  }
  return body;
}

/**
 * Monotonic guards so a slower, older response can't overwrite a newer one
 * (NEWS-104).
 *
 * Refreshes run concurrently by design: a 4-second poll, plus one after every
 * mutation (`withRefresh`). Without a guard the store simply takes whichever
 * response *resolves* last, which is not the same as the one issued last. The
 * visible symptom is a setting appearing to revert — change an interval, and a
 * poll that was already in flight answers with the pre-PATCH value and rewrites
 * the `<select>` until the next tick, up to 4 seconds later.
 *
 * A sequence number rather than an `AbortController`: the older request's
 * *answer* is what's unwanted, not the request itself, and cancelling a poll
 * that a mutation happened to overlap would throw away a legitimate refresh.
 *
 * Counters are module-scoped because there is exactly one store and one poll
 * per page. `refreshFeed` needs its own — the two endpoints are independent, and
 * a shared counter would let a feed response suppress a state one.
 */
let stateSeq = 0;
let stateApplied = 0;
let feedSeq = 0;
let feedApplied = 0;
let pulseSeq = 0;
let pulseApplied = 0;
let pulseArchiveKey = '';

/**
 * Fingerprints of the last `/api/state` and `/api/items` answers we actually
 * applied (NM-230).
 *
 * Every apply calls the store's `set()`, which runs kerf's morph over the whole
 * tree. On an idle 4-second poll the server returns a byte-identical answer, so
 * that morph is pure waste — and, the reported bug, it silently resets any DOM
 * node a developer edited by hand between polls. Comparing the *stringified*
 * response against the last applied one lets an unchanged poll skip the apply
 * entirely, so an idle tab does zero DOM work and a hand-edited node survives
 * until the data genuinely changes.
 *
 * A JSON string rather than a structural compare: the responses are already
 * plain JSON, `StateRespSchema`/`ItemsRespSchema` emit their fields in a stable
 * (schema-defined) order, so `JSON.stringify` is a cheap, exact fingerprint.
 * Module-scoped for the same reason the sequence counters are — one store, one
 * poll per page. Empty initially, so the first poll always applies.
 */
let lastStateJson = '';
let lastFeedJson = '';

/**
 * The server revision the last `/api/state` answer reflected (NM-231).
 *
 * The long-poll loop echoes it back as `?waitRev=<currentRev>`, so the server
 * holds the request until the revision moves past it. Updated from every winning
 * response — including one whose bytes the NM-230 dedup then skips — so a bump
 * that did not change the visible state (a spurious one, or a change to a field
 * this client already showed) still advances the wait and the poll does not
 * spin. Zero initially, so the first request never waits.
 */
let currentRev = 0;

/**
 * Clear the NM-230 poll fingerprints. **Tests only.** In the browser these vars
 * live for the page's lifetime — which is exactly what makes the
 * skip-when-unchanged work — but unit tests share this module across cases, so a
 * later test feeding a response an earlier test's fingerprint already matched
 * would wrongly skip the apply. Poll-driving tests call this in `beforeEach`.
 */
export function __resetPollFingerprints(): void {
  lastStateJson = '';
  lastFeedJson = '';
  currentRev = 0;
}

/**
 * Thread ids with a request out (NEWS-293).
 *
 * Not derivable from the store: a *refresh* of an open pane deliberately leaves
 * the pane's status `ready` so the old rows stay on screen, so "is one in
 * flight" and "is the pane showing a spinner" stopped being the same question
 * the moment the poll started calling `loadThread`.
 */
const threadsInFlight = new Set<string>();

/**
 * The topic whose edit dialog is open, for the poll to hold back (NEWS-366).
 *
 * Since NM-27 there is one editor — the Edit-topic dialog carries the focus
 * angles that used to have their own guidance dialog — so this is just
 * `renameTopicId`. Read from the store at request time rather than pushed on
 * open and close: the dialog can be dismissed several ways — the button, the
 * backdrop, Escape, a topic being deleted underneath it — and a push model needs
 * every one of those paths to remember to send a release. Deriving it from the
 * state that already decides whether the dialog is *rendered* cannot fall out of
 * step with what is on screen, and closing the tab ends the hold as surely as
 * closing the dialog does.
 */
function heldTopicId(): string | null {
  return appStore.state.value.renameTopicId;
}

/**
 * Who is signed in (NM-20, phase 3), parsed at the boundary like every other
 * response. Three shapes — signed out, unregistered, registered — which
 * `sessionGateState` turns into a gate screen.
 */
export async function fetchSession(): Promise<SessionResp> {
  return SessionRespSchema.parse(await request('/api/session'));
}

/**
 * Register the signed-in identity as a fresh account and adopt its re-issued
 * session (NM-20, phase 3). The server mints a brand-new account and Set-Cookies
 * the new session; the caller re-fetches `/api/session` to proceed into the app.
 */
export async function registerAccount(): Promise<void> {
  RegisterRespSchema.parse(await request('/api/register', { method: 'POST' }));
}

/** Sign out (NM-20, phase 3): clear the session cookie and return to the gate. */
export async function logoutSession(): Promise<void> {
  await request('/api/session/logout', { method: 'POST' });
}

export async function refreshState(opts: { wait?: boolean; signal?: AbortSignal } = {}): Promise<void> {
  const seq = ++stateSeq;
  try {
    // Build the query: the open-dialog hold (NEWS-366) and, when long polling
    // (NM-231), `waitRev` so the server holds the request until the revision
    // passes what this client last saw. `wait` is set only by the poll loop —
    // the first load and every mutation's own refetch omit it so they answer at
    // once. No body, so it stays a "simple" CORS request (no preflight).
    const params = new URLSearchParams();
    const held = heldTopicId();
    if (held !== null) params.set('holding', held);
    if (opts.wait === true) params.set('waitRev', String(currentRev));
    const query = params.toString();
    // `signal` lets the poll loop cancel a *held* long poll on sign-out (NM-231):
    // without it the request the loop is parked on completes minutes-of-quota
    // later at a signed-out session, and its `finally` fires a stray `/api/items`
    // — the exact "polling after sign-out" NM-41 forbids.
    const body = await request(
      query === '' ? '/api/state' : `/api/state?${query}`,
      opts.signal === undefined ? undefined : { signal: opts.signal },
    );
    const state = StateRespSchema.parse(body);
    // Errors are gated by the same check: a stale failure must not raise a
    // banner over state that a newer, successful response already applied.
    if (seq < stateApplied) return;
    stateApplied = seq;
    // Advance the wait cursor from the winning response — even one the dedup
    // below skips (a bump that did not change the visible bytes), or the poll
    // would immediately re-ask past the same rev and spin (NM-231).
    currentRev = state.rev;
    // Skip the whole apply when this answer is byte-identical to the last one
    // applied (NM-230). `setState` → the store's `set()` → a full morph; on an
    // idle poll that morph does nothing but reset a hand-edited DOM node. The
    // three consumers below all diff internally and would no-op on identical
    // input, so returning here changes only the wasted work — and the feed
    // still refreshes (its own guard) via the `finally`.
    //
    // Gated on `loaded`: an unloaded store must always apply, whatever the
    // fingerprint says, so the first poll of a fresh mount — or the first after
    // a sign-out reset — still sets `loaded: true` even if its bytes happen to
    // match a previous session's last answer.
    const nextStateJson = JSON.stringify(state);
    if (appStore.state.value.loaded && nextStateJson === lastStateJson) return;
    lastStateJson = nextStateJson;
    appStore.actions.setState(state);
    // Fire an OS notification if new stories arrived while unfocused (NEWS-38).
    // Inside the guard deliberately — it diffs against the last state it saw, so
    // feeding it a stale one would mis-report what's new.
    noteState(state);
    // Speak check progress for the polite live region (NM-92) — same guard, same
    // reason: it diffs `checking` and today's counts against the last state seen.
    appStore.actions.setCheckAnnouncement(noteCheckState(state));
    const nextPulseKey = JSON.stringify([
      state.latestItemIds,
      state.topics.map((topic) => [topic.id, topic.category, topic.subcategory]),
    ]);
    if (nextPulseKey !== pulseArchiveKey) {
      pulseArchiveKey = nextPulseKey;
      void refreshPulseSurfaces();
    }
  } catch (err) {
    // An aborted poll (sign-out, NM-231) is abandoned, not failed: no banner, no
    // follow-up — it was cancelled on purpose.
    if (opts.signal?.aborted === true) return;
    if (seq < stateApplied) return;
    stateApplied = seq;
    appStore.actions.setError(err instanceof Error ? err.message : String(err));
  } finally {
    // Skip the follow-ups when the request was aborted (sign-out): the poll is
    // being torn down, so bumping the tick or refetching the feed is the very
    // "polling after sign-out" the abort exists to stop (NM-41/NM-231).
    if (opts.signal?.aborted !== true) {
      // Heartbeat for the form-control dirty-flag re-sync (NM-230). NM-230's
      // skip-when-unchanged means an idle poll no longer re-renders the tree, but
      // `syncSelects`/`syncRadios` (NEWS-238, FR-3.5a) must still run each poll to
      // snap a server-bound control the user moved back onto the server's value —
      // that pass touches only form controls, never the tree — so bump the tick
      // here whether or not the state applied.
      pollTick.value = pollTick.value + 1;
      // The feed lives on its own endpoint now (NEWS-76); refresh it in step.
      // In `finally` so a stale response returning early still refreshes it —
      // `refreshFeed` has its own guard, and skipping it here would drop the feed
      // refresh that `withRefresh` is relying on.
      await refreshFeed();
    }
  }
}

/**
 * Submit the mock upgrade checkout (NM-225/226). On success the plan changed
 * server-side, so it refreshes `/api/state` (new caps) and closes the checkout; a
 * decline / invalid card returns a non-ok response whose body carries a human
 * message, shown *inside* the checkout rather than as a global error banner.
 */
export async function submitUpgrade(plan: string, card: string): Promise<void> {
  appStore.actions.updateCheckout({ submitting: true, error: null });
  try {
    await request('/api/upgrade', { method: 'POST', body: JSON.stringify({ plan, card }) });
    await refreshState();
    appStore.actions.closeCheckout();
  } catch (err) {
    appStore.actions.updateCheckout({ submitting: false, error: upgradeErrorMessage(err) });
  }
}

function upgradeErrorMessage(err: unknown): string {
  if (err instanceof RequestError && typeof err.body === 'object' && err.body !== null && 'message' in err.body) {
    const message = err.body.message;
    if (typeof message === 'string' && message !== '') return message;
  }
  return 'Payment could not be completed. Please try again.';
}

function pulsePath(
  scope: { kind: 'topic' | 'category'; id: string; subcategory?: string | null },
  days: 7 | 30 | 90,
): string {
  const params = new URLSearchParams({ days: String(days) });
  if (scope.kind === 'topic') params.set('topic', scope.id);
  else {
    params.set('category', scope.id);
    if (scope.subcategory !== undefined && scope.subcategory !== null) params.set('subcategory', scope.subcategory);
  }
  return `/api/pulse?${params.toString()}`;
}

/** Refresh the rail and the two compact pulse surfaces (NEWS-453). */
export async function refreshPulseSurfaces(): Promise<void> {
  const seq = ++pulseSeq;
  const state = appStore.state.value;
  const topicId = state.soloTopicIds.length === 1 ? state.soloTopicIds[0] : undefined;
  const category = effectiveCategoryFilter(state.categoryFilter, state.soloTopicIds);
  try {
    const [sparklines, topicPulse, categoryPulse] = await Promise.all([
      request('/api/pulse/topics').then((body) => TopicSparklineRespSchema.parse(body)),
      topicId === undefined
        ? Promise.resolve(null)
        : request(pulsePath({ kind: 'topic', id: topicId }, 30)).then((body) => PulseRespSchema.parse(body)),
      category === null
        ? Promise.resolve(null)
        : request(pulsePath({ kind: 'category', id: category.category, subcategory: category.subcategory }, 30)).then(
            (body) => PulseRespSchema.parse(body),
          ),
    ]);
    if (seq < pulseApplied) return;
    pulseApplied = seq;
    appStore.actions.setPulseSparklines(sparklines.byTopic);
    appStore.actions.setPulseSummaries(topicPulse, categoryPulse);
  } catch {
    // These are supplemental reading aids. A failed refresh leaves the last
    // good pulse in place; the feed and its four-second state poll stay healthy.
  }
}

/** Open or change the full pulse drill-down (NEWS-453). */
export async function loadPulseDetail(
  scope: { kind: 'topic' | 'category'; id: string; subcategory?: string | null },
  days: 7 | 30 | 90,
): Promise<void> {
  appStore.actions.openPulse({ ...scope, days });
  try {
    const pulse: PulseResp = PulseRespSchema.parse(await request(pulsePath(scope, days)));
    // The store drops an answer a newer request has superseded (NM-HFXEYX).
    appStore.actions.setPulseDetail(pulse);
  } catch (err) {
    // A superseded request's failure is as stale as its answer would be.
    if (!isPendingPulse({ ...scope, days })) return;
    appStore.actions.failPulse();
    appStore.actions.setError(err instanceof Error ? err.message : String(err));
  }
}

/**
 * Fetch the feed page for the current view from `/api/items` (NEWS-76).
 *
 * Builds the query from the active view — review mode, else Solo + Saved +
 * Search — plus the current `feedLimit`. Called on the poll, on a view change,
 * and on "Show more"; each call fetches the newest `feedLimit` matches, so a
 * poll naturally folds in new stories at the top.
 */
/**
 * Acknowledge the "database was set aside" notice (NEWS-340).
 *
 * Fire-and-forget: the banner is already gone from the client's state, and a
 * failed dismissal simply means it reappears on the next poll — which is the
 * right way for this one to fail. Nothing is deleted by dismissing.
 */
export async function dismissQuarantine(): Promise<void> {
  try {
    await request('/api/quarantine/dismiss', { method: 'POST' });
  } catch {
    // Reappearing is a better failure than a banner saying the dismissal failed.
  }
}

export async function refreshFeed(): Promise<void> {
  const s = appStore.state.value;
  const params = new URLSearchParams({ limit: String(s.feedLimit) });
  if (s.reviewTopicIds.length > 0) {
    params.set('mode', 'review');
    params.set('topics', s.reviewTopicIds.join(','));
  } else {
    if (s.soloTopicIds.length > 0) params.set('topics', s.soloTopicIds.join(','));
    if (s.savedFilter) params.set('saved', '1');
    const q = s.searchQuery.trim();
    if (q !== '') params.set('q', q);
    // Resolved server-side (NEWS-97): the client holds one page, so filtering
    // here would miss matches deeper in history — the NEWS-74 bug.
    const category = effectiveCategoryFilter(s.categoryFilter, s.soloTopicIds);
    if (category !== null) {
      params.set('category', category.category);
      if (category.subcategory !== null) params.set('subcategory', category.subcategory);
    }
  }
  const seq = ++feedSeq;
  try {
    const resp = ItemsRespSchema.parse(await request(`/api/items?${params.toString()}`));
    // Same ordering guard as `refreshState`, and it matters more here: the query
    // is built from the *current* view, so a response for the previous search
    // term or Solo set landing late would repopulate the feed with rows the
    // filters have already excluded.
    if (seq < feedApplied) return;
    feedApplied = seq;
    // Same skip as `refreshState` (NM-230): an unchanged feed page needn't
    // re-morph. Guarded around `setFeed` only — the open-thread follow below
    // still runs, and `loadThread` no-ops when the thread's size is unchanged,
    // so a growing thread is unaffected either way.
    const nextFeedJson = JSON.stringify({ items: resp.items, total: resp.total, threads: resp.threads });
    if (nextFeedJson !== lastFeedJson) {
      lastFeedJson = nextFeedJson;
      appStore.actions.setFeed({ items: resp.items, total: resp.total, threads: resp.threads });
    }
    // An open pane follows its thread (NEWS-293). The badge shape in `threads`
    // has just been refreshed, so this is the one moment the client can know a
    // thread grew — and `loadThread` compares the new size against what the
    // pane holds and returns without a request when they agree, so the poll
    // costs nothing on the overwhelmingly common unchanged tick. No new
    // reactive edge: the existing guard *is* the mechanism.
    const expanded = appStore.state.value.expandedItemId;
    if (expanded !== null) void loadThread(expanded);
  } catch (err) {
    if (seq < feedApplied) return;
    feedApplied = seq;
    appStore.actions.setError(err instanceof Error ? err.message : String(err));
  }
}

/**
 * Load one story's thread for the detail pane (NEWS-282).
 *
 * **On expand, never on render.** The feed already carries each story's thread
 * *shape* (`threads`, one small record per multi-story thread), so this is only
 * ever called for a card someone actually opened — a fetch per card in a
 * hundred-story feed for a pane nobody looked at is the thing this avoids.
 *
 * Three early returns, each a decision rather than a guard:
 *
 * - **No summary means a thread of one** (FR-29.6), the ordinary case. There is
 *   nothing to fetch and the pane says so in a line; asking anyway would spend a
 *   request per expansion to be told what the feed already said.
 * - **A cached thread of the same size is reused**, so collapse/re-expand is
 *   free. Keyed on size rather than on time: the 4-second poll refreshes the
 *   badge, so a thread that has grown announces itself and is refetched.
 * - **An in-flight request is not duplicated.** The button and the card body are
 *   one handler, but a double-click still arrives as two clicks.
 *
 * A failure lands in the pane, not in the page banner: a background read for one
 * card is not something to raise a red banner across the app over, and the pane
 * is where the reader is looking. It offers a retry rather than clearing itself,
 * and is never cached — re-opening the card tries again.
 *
 * **A pane that already has rows is refreshed in place** (NEWS-293), which is
 * the whole difference between this being a fix and being a flicker. `refreshFeed`
 * calls this for the open card on every 4-second poll, so the first-load path —
 * `status: 'loading'`, which the pane renders as "Looking up the story so far…"
 * — would replace a correct timeline with a placeholder each time the thread
 * grew. The old rows stay on screen until the new ones land: they are still
 * true, just one instalment short for a moment.
 *
 * For the same reason a **failed refresh keeps what is on screen** rather than
 * swapping a good timeline for an error with a retry button. The reader did not
 * ask for anything; a background refresh that fails should be silent, and the
 * next poll tries again anyway.
 */
export async function loadThread(id: string): Promise<void> {
  const state = appStore.state.value;
  const summary = state.threads[id];
  if (summary === undefined || summary.size < 2) return;
  const cached = state.threadPanes[id];
  if (cached !== undefined) {
    if (cached.status === 'loading') return;
    if (cached.status === 'ready' && cached.size === summary.size) return;
  }
  // Tracked here rather than inferred from `status: 'loading'`, because a
  // refresh deliberately does not set that status — without this, every poll
  // during a slow request would start another one.
  if (threadsInFlight.has(id)) return;
  const refreshing = cached?.status === 'ready';
  if (!refreshing) appStore.actions.setThreadPane(id, { status: 'loading' });
  threadsInFlight.add(id);
  try {
    const resp = ThreadRespSchema.parse(await request(`/api/items/${encodeURIComponent(id)}/thread`));
    appStore.actions.setThreadPane(id, { status: 'ready', items: resp.items, size: resp.items.length });
  } catch (err) {
    if (refreshing) return;
    appStore.actions.setThreadPane(id, {
      status: 'error',
      message: err instanceof Error ? err.message : String(err),
    });
  } finally {
    threadsInFlight.delete(id);
  }
}

/** Run an action, surface its error in the banner, then refresh app state. */
export async function withRefresh(fn: () => Promise<unknown>): Promise<void> {
  try {
    await fn();
    appStore.actions.setError(null);
  } catch (err) {
    appStore.actions.setError(err instanceof Error ? err.message : String(err));
  }
  await refreshState();
}

/** Ask for "did you mean?" overlap suggestions for a typed name (NM-148/153). */
export async function suggestSimilar(name: string): Promise<SuggestSimilarResp> {
  const body = await request('/api/topics/suggest-similar', { method: 'POST', body: JSON.stringify({ name }) });
  return SuggestSimilarRespSchema.parse(body);
}

/**
 * Create a topic and return the created (or re-subscribed) subject (NM-163).
 *
 * Still goes through `withRefresh`, so a rejection (a 409 duplicate) surfaces in
 * the banner and never rejects here — the returned topic is `null` in that case.
 * On success the caller inspects the topic: whether it came back **awaiting a
 * sense** (an ambiguous name, NM-163) and, if so, opens the sense picker, or came
 * back **disabled** (`paused` — added over the plan cap, NM-200) and, if so,
 * toasts that it landed off; the old callers that ignore the return are unaffected.
 *
 * Freeform guidance was removed in NM-124; a topic is created with just its name
 * (the closed focus vocabulary is set later via PATCH). A discovered topic's
 * classification still travels with the create — see `addSuggestedTopic` below.
 */
export async function addTopic(name: string): Promise<Topic | null> {
  let created: Topic | null = null;
  await withRefresh(async () => {
    const body = await request('/api/topics', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    created = TopicSchema.parse(body);
  });
  return created;
}

/**
 * Choose which sense an ambiguous topic means (NM-163, docs/47).
 *
 * Ends the "needs attention" gate: the server records the sense and fires the
 * topic's deferred first check. `withRefresh` so a rejection lands in the banner
 * and the state refreshes to drop the picker and show the topic checking.
 */
export function chooseTopicSense(id: string, sense: string): Promise<void> {
  return withRefresh(() =>
    request(`/api/topics/${encodeURIComponent(id)}/sense`, {
      method: 'POST',
      body: JSON.stringify({ sense }),
    }),
  );
}

/**
 * Create a topic from a discovery suggestion (NEWS-126).
 *
 * The classification travels with the create rather than in a follow-up PATCH:
 * creating a topic fires its first check immediately (FR-1.12), so a second
 * request would land after that check had already run unclassified. (Freeform
 * guidance was removed in NM-124.)
 *
 * Errors are thrown rather than swallowed into the global banner: the caller
 * shows them on the card, next to the button that failed.
 */
export async function addSuggestedTopic(suggestion: TopicSuggestion): Promise<void> {
  await request('/api/topics', {
    method: 'POST',
    body: JSON.stringify({
      name: suggestion.name,
      ...(suggestion.classification === null
        ? {}
        : {
            category: suggestion.classification.category,
            ...(suggestion.classification.subcategory === null
              ? {}
              : { subcategory: suggestion.classification.subcategory }),
          }),
    }),
  });
  await refreshState();
}

/** Ask for topic suggestions (FR-24.1). */
export async function discoverTopics(
  scope: DiscoverReq['scope'],
  limit?: number,
  seen?: string[],
): Promise<DiscoverResp> {
  const body = await request('/api/discover', {
    method: 'POST',
    body: JSON.stringify({
      scope,
      ...(limit === undefined ? {} : { limit }),
      // Only sent for "More" (NEWS-136) — an empty list would needlessly change
      // the cache key and turn a free repeat request into a billed one.
      ...(seen === undefined || seen.length === 0 ? {} : { seen }),
    }),
  });
  return DiscoverRespSchema.parse(body);
}

export function deleteTopic(id: string): Promise<void> {
  return withRefresh(() => request(`/api/topics/${encodeURIComponent(id)}`, { method: 'DELETE' }));
}

/**
 * How many stories a topic has (NEWS-139).
 *
 * Reuses the feed endpoint's `total` rather than adding one of its own — asking
 * for a single item gives the count without the rows. Deliberately not on
 * `/api/state`: that is polled every four seconds by every client, and this is a
 * `GROUP BY` over every story needed once, when a dialog opens.
 */
export async function countItemsForTopic(id: string): Promise<number> {
  const body = await request(`/api/items?topics=${encodeURIComponent(id)}&limit=1`);
  return ItemsRespSchema.parse(body).total;
}

/**
 * Rename a topic, optionally discarding its existing stories (NEWS-139).
 *
 * Errors are thrown rather than folded into the global banner: a duplicate name
 * is something the user fixes in the dialog they are already looking at.
 */
/**
 * Save the edit dialog (NEWS-407 added the section).
 *
 * `category` is `undefined` for "don't touch it" and `null` for "let Newsmonger
 * decide" — the two are different and the route relies on the distinction:
 * `null` clears the category **and** resets `categorySource` to `auto`, which is
 * what makes the topic eligible for automatic classification again (FR-22.7).
 * Sending it in the same PATCH as the name so one Save is one request, and the
 * dialog cannot half-apply.
 */
export async function renameTopic(
  id: string,
  name: string,
  clearItems: boolean,
  category?: { category: string | null; subcategory: string | null },
  focus?: string[],
): Promise<void> {
  await request(`/api/topics/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      name,
      ...(clearItems ? { clearItems: true } : {}),
      ...(category ?? {}),
      // Always sent when the dialog collected it (NM-27), including `[]` so
      // unticking every chip clears the focus; the server clears the topic's
      // stale Brave query on any focus write.
      ...(focus !== undefined ? { focus } : {}),
    }),
  });
  await refreshState();
  await refreshFeed();
}

/**
 * Put back the stories a clear removed (NEWS-145).
 *
 * The window is server-side and short, so this can legitimately fail with a 410
 * after the user has already seen the Undo — `request` turns that into a thrown
 * Error carrying the server's message, which the caller shows as a plain notice
 * rather than a red banner. Expiring is not an error condition.
 */
export async function restoreClearedItems(id: string): Promise<void> {
  await request(`/api/topics/${encodeURIComponent(id)}/restore-cleared`, { method: 'POST' });
  await refreshState();
  await refreshFeed();
}

export function setTopicPaused(id: string, paused: boolean): Promise<void> {
  return withRefresh(() =>
    request(`/api/topics/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ paused }) }),
  );
}

/**
 * Enable a subject (turn it on) — capacity-aware (NM-201). Resolves `null` on
 * success, or the `over_capacity` info when the plan cap refuses it, so the caller
 * can open the pick-to-disable dialog. Deliberately **not** `withRefresh`: that
 * swallows every rejection into the error banner, which would hide the 409 this
 * exists to surface. A different error still lands in the banner. Refreshes state
 * (and the feed, since enabling reveals a subject's articles) either way.
 */
export async function enableTopic(id: string): Promise<OverCapacity | null> {
  let over: OverCapacity | null = null;
  try {
    await request(`/api/topics/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ paused: false }),
    });
    appStore.actions.setError(null);
  } catch (err) {
    over = overCapacityOf(err);
    if (over === null) appStore.actions.setError(err instanceof Error ? err.message : String(err));
  }
  await refreshState();
  await refreshFeed();
  return over;
}

/**
 * Atomically enable one set of subjects and disable another (NM-201, the swap the
 * capacity dialog commits). `null` on success; the `over_capacity` info if the
 * post-swap counts would still exceed a cap (the whole swap is rolled back
 * server-side). Same non-`withRefresh` reasoning as {@link enableTopic}.
 */
export async function swapCapacity(enable: string[], disable: string[]): Promise<OverCapacity | null> {
  let over: OverCapacity | null = null;
  try {
    await request('/api/topics/capacity', { method: 'POST', body: JSON.stringify({ enable, disable }) });
    appStore.actions.setError(null);
  } catch (err) {
    over = overCapacityOf(err);
    if (over === null) appStore.actions.setError(err instanceof Error ? err.message : String(err));
  }
  await refreshState();
  await refreshFeed();
  return over;
}

export function setNotifyOnNewItems(notifyOnNewItems: boolean): Promise<void> {
  return withRefresh(() => request('/api/settings', { method: 'PATCH', body: JSON.stringify({ notifyOnNewItems }) }));
}

/** Save a browser Web Push subscription to this account (NM-251). */
export async function savePushSubscription(sub: {
  endpoint: string;
  keys?: { p256dh?: string; auth?: string };
}): Promise<void> {
  await request('/api/push/subscribe', { method: 'POST', body: JSON.stringify(sub) });
}

/** Delete a browser Web Push subscription by endpoint (NM-251). */
export async function deletePushSubscription(endpoint: string): Promise<void> {
  await request('/api/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) });
}

/** Light, dark, or follow the system (FR-3.74, NEWS-334). */
export function updateTheme(theme: 'auto' | 'light' | 'dark'): Promise<void> {
  return withRefresh(() => request('/api/settings', { method: 'PATCH', body: JSON.stringify({ theme }) }));
}

/**
 * Set where the user is; '' clears it (NEWS-393, FR-35.1).
 *
 * Trimmed but otherwise sent as typed — no normalising, no case folding, no
 * transliteration. The value is read by a model, not matched against a table,
 * so "Lisboa" and "東京" are as correct as anything we could rewrite them into.
 */
export function updateLocation(location: string): Promise<void> {
  return withRefresh(() => request('/api/settings', { method: 'PATCH', body: JSON.stringify({ location }) }));
}

/**
 * Save the ticked reader profiles, by id (NEWS-383).
 *
 * Ids, never labels — rewording a chip must not orphan everyone who ticked it.
 * An empty array is a real value meaning "ticked nothing", not "unset".
 */
export function updateProfiles(profiles: readonly string[]): Promise<void> {
  return withRefresh(() =>
    request('/api/settings', { method: 'PATCH', body: JSON.stringify({ profiles: [...profiles] }) }),
  );
}

/**
 * Delete every story, keeping topics and settings (NEWS-255).
 *
 * Refreshes afterwards because the feed and every sidebar count are now wrong —
 * the 4-second poll would fix it eventually, and "eventually" after a
 * destructive action reads as the action not having worked.
 */
export async function clearAllStories(): Promise<{ cleared: number; cancelledChecks: number }> {
  const body = await request('/api/items/clear', { method: 'POST' });
  const { cleared, cancelledChecks } = ClearItemsRespSchema.parse(body);
  await refreshState();
  return { cleared, cancelledChecks };
}

/**
 * Delete every topic, and with it every story and run (FR-31.1, NEWS-328).
 *
 * Refreshes afterwards for the same reason `clearAllStories` does: the rail, the
 * feed and every count are now wrong, and waiting for the 4-second poll after a
 * destructive action reads as the action not having worked.
 */
export async function deleteAllTopics(): Promise<{ deleted: number; cancelledChecks: number }> {
  const body = await request('/api/topics/clear', { method: 'POST' });
  const { deleted, cancelledChecks } = ClearTopicsRespSchema.parse(body);
  await refreshState();
  return { deleted, cancelledChecks };
}

// The set-aside listing + recover calls (the doc-33 Recovery UI) were removed in
// NM-171: a quarantined database is restored at the terminal now (store.ts names
// it and the manual step at startup), so the client no longer fetches candidates
// or posts a recovery. The FR-4.17 quarantine banner is unchanged.

/**
 * Read a shared topic list back in (FR-30.5–30.9, NEWS-318).
 *
 * The file's text is posted as-is rather than parsed here: the server validates
 * it against the same schema either way, and doing it in one place means the
 * client cannot accept something the server would refuse. A parse error and a
 * schema error then arrive by the same road, with the server's wording.
 *
 * `refreshState()` afterwards because the topics rail is now wrong — nothing
 * else fires, since an import runs no check (FR-30.8).
 */
export async function importTopics(fileText: string): Promise<ImportTopicsResp> {
  const body = await request('/api/import-topics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: fileText,
  });
  const parsed = ImportTopicsRespSchema.parse(body);
  await refreshState();
  return parsed;
}

/**
 * Read an exported story archive back in (FR-30.10–30.14, NEWS-319).
 *
 * Same shape as `importTopics`: the file's text goes up as-is so one schema on
 * the server decides what is acceptable, and the client cannot accept something
 * the server would refuse.
 */
export async function importStories(fileText: string): Promise<ImportStoriesResp> {
  const body = await request('/api/import-stories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: fileText,
  });
  const parsed = ImportStoriesRespSchema.parse(body);
  // Topics *and* the feed may both have changed — an import can create topics.
  await refreshState();
  return parsed;
}

export function setItemSaved(id: string, saved: boolean): Promise<void> {
  return withRefresh(() =>
    request(`/api/items/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ saved }) }),
  );
}

export function setItemOffTopic(id: string, offTopic: boolean): Promise<void> {
  return (async () => {
    await withRefresh(() =>
      request(`/api/items/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ offTopic }) }),
    );
    await refreshPulseSurfaces();
  })();
}

/**
 * Tell the server the app is in front of the user right now.
 *
 * Only sent when the page is both visible and focused — a visible but
 * unfocused window on a second monitor isn't someone using the app, and this
 * signal is what permits scheduled checks to spend subscription quota.
 */
export function reportForeground(): Promise<unknown> {
  return request('/api/foreground', { method: 'POST' }).catch(() => null);
}
